import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { useQuery } from '@tanstack/react-query'
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { getLaboratorios } from '@/modules/users/api/groupsApi'
import { PAISES_IA } from '../types/consumoIA'
import type { ConsumoIA, ConsumoIACreateInput, ConsumoIAUpdateInput, PaisIA } from '../types/consumoIA'

const MESES = [
  { value: 1, label: 'Enero' }, { value: 2, label: 'Febrero' },
  { value: 3, label: 'Marzo' }, { value: 4, label: 'Abril' },
  { value: 5, label: 'Mayo' }, { value: 6, label: 'Junio' },
  { value: 7, label: 'Julio' }, { value: 8, label: 'Agosto' },
  { value: 9, label: 'Septiembre' }, { value: 10, label: 'Octubre' },
  { value: 11, label: 'Noviembre' }, { value: 12, label: 'Diciembre' },
]

const schema = z.object({
  laboratorio_id: z.coerce.number().min(1, 'Selecciona un laboratorio'),
  mes: z.coerce.number().min(1).max(12),
  anio: z.coerce.number().min(2020).max(2099),
  cantidad_imagenes: z.coerce.number().min(0),
  costo_por_imagen: z.coerce.number().min(0),
  costo_por_imagen_proveedor: z.coerce.number().min(0),
  estado_cobro: z.enum(['pendiente', 'cobrado']),
  estado_pago_proveedor: z.enum(['pendiente', 'pagado']),
  pais: z.enum(['ecuador', 'colombia', 'peru', 'mexico', 'chile', 'argentina', 'otro']),
})

type FormData = z.infer<typeof schema>

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  record?: ConsumoIA | null
  onSubmit: (data: ConsumoIACreateInput | ConsumoIAUpdateInput, id?: number) => void
  isSubmitting: boolean
  viewMode?: 'laboratorio' | 'proveedor'
}

export function AdminGestionDialog({ open, onOpenChange, record, onSubmit, isSubmitting, viewMode = 'laboratorio' }: Props) {
  const isEditing = !!record
  const [labPopoverOpen, setLabPopoverOpen] = useState(false)
  const [selectedLabId, setSelectedLabId] = useState<number | null>(null)

  const { data: laboratorios = [] } = useQuery({
    queryKey: ['laboratorios-list'],
    queryFn: getLaboratorios,
    enabled: open,
  })

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      mes: new Date().getMonth() + 1,
      anio: new Date().getFullYear(),
      cantidad_imagenes: 0,
      costo_por_imagen: 0.10,
      costo_por_imagen_proveedor: 0.05,
      estado_cobro: 'pendiente' as const,
      estado_pago_proveedor: 'pendiente' as const,
      pais: 'ecuador' as const,
    },
  })

  useEffect(() => {
    if (open) {
      if (record) {
        reset({
          laboratorio_id: record.laboratorio_id,
          mes: record.mes,
          anio: record.anio,
          cantidad_imagenes: record.cantidad_imagenes,
          costo_por_imagen: record.costo_por_imagen,
          costo_por_imagen_proveedor: record.costo_por_imagen_proveedor,
          estado_cobro: record.estado_cobro,
          estado_pago_proveedor: record.estado_pago_proveedor,
          pais: record.pais,
        })
      } else {
        reset({
          mes: new Date().getMonth() + 1,
          anio: new Date().getFullYear(),
          cantidad_imagenes: 0,
          costo_por_imagen: 0.10,
          costo_por_imagen_proveedor: 0.05,
          estado_cobro: 'pendiente',
          estado_pago_proveedor: 'pendiente',
          pais: 'ecuador',
        })
        setSelectedLabId(null)
      }
    }
  }, [open, record, reset])

  const cantidadVal = watch('cantidad_imagenes') || 0
  const costoPorImagenVal = watch('costo_por_imagen') || 0
  const costoPorImagenProveedorVal = watch('costo_por_imagen_proveedor') || 0
  const costoPreview = viewMode === 'proveedor'
    ? (Number(cantidadVal) * Number(costoPorImagenProveedorVal)).toFixed(2)
    : (Number(cantidadVal) * Number(costoPorImagenVal)).toFixed(2)

  const handleFormSubmit = (data: FormData) => {
    if (isEditing) {
      onSubmit({
        cantidad_imagenes: data.cantidad_imagenes,
        costo_por_imagen: viewMode === 'laboratorio' ? data.costo_por_imagen : undefined,
        costo_por_imagen_proveedor: viewMode === 'proveedor' ? data.costo_por_imagen_proveedor : undefined,
        estado_cobro: data.estado_cobro,
        estado_pago_proveedor: data.estado_pago_proveedor,
        pais: data.pais as PaisIA,
      }, record.id)
    } else {
      onSubmit(data as ConsumoIACreateInput)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Editar registro' : 'Nuevo registro'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
          {!isEditing && (
            <div className="space-y-1.5">
              <Label>Laboratorio</Label>
              <Popover open={labPopoverOpen} onOpenChange={setLabPopoverOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className={cn(
                      'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:border-zinc-400',
                      !selectedLabId && 'text-muted-foreground'
                    )}
                  >
                    <span className="truncate">
                      {selectedLabId
                        ? laboratorios.find((l: any) => l.grupo_id === selectedLabId)?.grupo_nombre
                        : 'Seleccionar laboratorio'}
                    </span>
                    <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0 ml-2" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Buscar laboratorio..." />
                    <CommandList>
                      <CommandEmpty>Sin resultados.</CommandEmpty>
                      <CommandGroup>
                        {laboratorios.map((lab: any) => (
                          <CommandItem
                            key={lab.grupo_id}
                            value={lab.grupo_nombre}
                            onSelect={() => {
                              setSelectedLabId(lab.grupo_id)
                              setValue('laboratorio_id', lab.grupo_id)
                              setLabPopoverOpen(false)
                            }}
                          >
                            <Check
                              className={cn(
                                'mr-2 h-4 w-4',
                                selectedLabId === lab.grupo_id ? 'opacity-100' : 'opacity-0'
                              )}
                            />
                            {lab.grupo_nombre}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {errors.laboratorio_id && (
                <p className="text-xs text-red-500">{errors.laboratorio_id.message}</p>
              )}
            </div>
          )}

          {!isEditing && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Mes</Label>
                <Select
                  defaultValue={String(new Date().getMonth() + 1)}
                  onValueChange={(v) => setValue('mes', Number(v))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MESES.map((m) => (
                      <SelectItem key={m.value} value={String(m.value)}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Año</Label>
                <Input
                  type="number"
                  {...register('anio')}
                />
                {errors.anio && <p className="text-xs text-red-500">{errors.anio.message}</p>}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Cantidad de imágenes</Label>
              <Input
                type="number"
                min="0"
                {...register('cantidad_imagenes')}
              />
              {errors.cantidad_imagenes && (
                <p className="text-xs text-red-500">{errors.cantidad_imagenes.message}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Costo por imagen (USD)</Label>
              {viewMode === 'laboratorio' ? (
                <Input
                  type="number"
                  min="0"
                  step="0.0001"
                  {...register('costo_por_imagen')}
                />
              ) : (
                <Input
                  type="number"
                  min="0"
                  step="0.0001"
                  {...register('costo_por_imagen_proveedor')}
                />
              )}
              {viewMode === 'laboratorio' && errors.costo_por_imagen && (
                <p className="text-xs text-red-500">{errors.costo_por_imagen.message}</p>
              )}
              {viewMode === 'proveedor' && errors.costo_por_imagen_proveedor && (
                <p className="text-xs text-red-500">{errors.costo_por_imagen_proveedor.message}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Estado de cobro</Label>
              <Select
                defaultValue={record?.estado_cobro ?? 'pendiente'}
                onValueChange={(v) => setValue('estado_cobro', v as 'pendiente' | 'cobrado')}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pendiente">Pendiente</SelectItem>
                  <SelectItem value="cobrado">Facturado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Estado de pago</Label>
              <Select
                defaultValue={record?.estado_pago_proveedor ?? 'pendiente'}
                onValueChange={(v) => setValue('estado_pago_proveedor', v as 'pendiente' | 'pagado')}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pendiente">Pendiente</SelectItem>
                  <SelectItem value="pagado">Pagado</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>País</Label>
            <Select
              defaultValue={record?.pais ?? 'ecuador'}
              onValueChange={(v) => setValue('pais', v as PaisIA)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAISES_IA.map((p) => (
                  <SelectItem key={p.value} value={p.value}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-lg bg-violet-500/10 border border-violet-500/20 px-4 py-3">
            <p className="text-sm text-violet-700 dark:text-violet-400">
              {viewMode === 'proveedor' ? 'Costo al proveedor' : 'Costo para el cliente'}:{' '}
              <span className="font-semibold">$ {costoPreview}</span>
            </p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="bg-violet-800 hover:bg-violet-900 text-white"
            >
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? 'Guardar cambios' : 'Crear registro'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
